import React, { useEffect, useState } from "react";
import AccountsMenu from "./AccountsMenu";
import { NavLink, useNavigate } from "react-router-dom";
import config from "../../Config";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { scrollToTop } from "../Shared/ScrollOnTop";

const LEVEL1_ROOT_CODES = [10, 20, 30, 40, 50, 60, 70];

function getDefaultFinancialYear() {
  const stored = localStorage.getItem("DefaultFYear");
  if (stored && String(stored).trim() !== "") {
    return parseInt(stored, 10);
  }
  const now = new Date();
  const month = now.getMonth() + 1;
  const calendarYear = now.getFullYear();
  return month >= 4 ? calendarYear : calendarYear - 1;
}

function getNextLevel1Code(existingLevel1) {
  const used = new Set(
    (existingLevel1 || [])
      .map((a) => parseInt(a.Account_Code, 10))
      .filter((n) => LEVEL1_ROOT_CODES.includes(n))
  );
  const next = LEVEL1_ROOT_CODES.find((code) => !used.has(code));
  return next != null ? String(next) : String(LEVEL1_ROOT_CODES[0]);
}

function getNextLevel2Code(accounts, parentCode) {
  const firstcode = String(parentCode).padStart(2, "0");
  const level2 = (accounts || []).filter(
    (item) =>
      item.iLevel === 2 &&
      String(item.Account_Code).startsWith(firstcode) &&
      item.Account_Code.length === 5
  );
  if (level2.length === 0) {
    return `${firstcode}101`;
  }
  const lastItem = level2[level2.length - 1];
  return String(parseInt(lastItem.Account_Code, 10) + 1);
}

function getNextLevel3Code(accounts, parentSecondCode) {
  const prefix = String(parentSecondCode);
  const level3 = (accounts || []).filter(
    (item) =>
      item.iLevel === 3 &&
      String(item.Account_Code).startsWith(prefix) &&
      item.Account_Code.length === 9
  );
  if (level3.length === 0) {
    return `${prefix}1001`;
  }
  const lastItem = level3[level3.length - 1];
  return String(parseInt(lastItem.Account_Code, 10) + 1);
}

function AddNewAccount(props) {
  const [AccessKey, setAccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID, setUserID] = useState(localStorage.getItem("ID"));
  const [MemberType, setMemberType] = useState(
    localStorage.getItem("MemberType")
  );
  const [FYear, setFYear] = useState(getDefaultFinancialYear());
  const [Accounts, setAccounts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [FirstAccountId, setFirstAccountId] = useState(0);
  const [SecondAccountId, setSecondAccountId] = useState(0);
  const [ThirdAccountId, setThirdAccountId] = useState(0);

  const [FirstLevelCode, setFirstLevelCode] = useState("10");
  const [SecondLevelCode, setSecondLevelCode] = useState("");
  const [ThirdLevelCode, setThirdLevelCode] = useState("");

  const [numOfItemsinSecond, setnumOfItemsinSecond] = useState();

  const [LevelID, setLevelID] = useState(1);
  const [ParentAccountID, setParentAccountID] = useState(0);

  const [AccountCode, setAccountCode] = useState("10");

  const [AccountDetail, setAccountDetail] = useState("");
  const [AccountRemarks, setAccountRemarks] = useState("");

  const [Level1Status, setLevel1Status] = useState(false);
  const [Level2Status, setLevel2Status] = useState(true);
  const [Level3Status, setLevel3Status] = useState(true);

  const [Level1Code, setLevel1Code] = useState("");

  // What account level needs to be added.

  const [AccountLevel, setAccountLevel] = useState(1);
  const navigate = useNavigate();

  const loadAccounts = (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setInitialLoading(true);
    }
    const data = {
      AccessKey: AccessKey,
      UserID: UserID,
      MemberType: MemberType,
      FYear: FYear,
      pageNo: 1,
      pageSize: 1000,
      pAccountID: ParentAccountID,
    };

    return axios({
      method: "post",
      url: config.base_url + "Accounts/GetAccountsByYearWithDeleted",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then((response) => {
        const rows = Array.isArray(response.data?.ListofRecords)
          ? response.data.ListofRecords
          : [];
        setAccounts(rows);
        if (response.data?.status_code === 2) {
          toast.error(
            response.data?.status_message || "Could not load accounts.",
            { position: toast.POSITION.TOP_RIGHT }
          );
        }
        return rows;
      })
      .catch(() => {
        setAccounts([]);
        toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
        return [];
      })
      .finally(() => {
        if (!silent) {
          setInitialLoading(false);
        }
      });
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const applyLevel1Defaults = (accountRows) => {
    const level1 = (accountRows || []).filter((item) => item.iLevel === 1);
    const nextCode = getNextLevel1Code(level1);
    setFirstLevelCode(nextCode);
    setAccountCode(nextCode);
  };

  const refreshAfterSave = (accountRows, savedLevel, context) => {
    if (savedLevel === 1) {
      applyLevel1Defaults(accountRows);
      setLevel1Status(false);
      setLevel2Status(true);
      setLevel3Status(true);
      return;
    }

    if (savedLevel === 2 && context.firstAccountId) {
      const parentCode =
        context.level1Code ||
        accountRows.find(
          (a) => String(a.ID) === String(context.firstAccountId)
        )?.Account_Code ||
        "";
      const nextCode = getNextLevel2Code(accountRows, parentCode);
      setFirstLevelCode(String(parentCode).padStart(2, "0"));
      setSecondLevelCode(nextCode);
      setAccountCode(nextCode);
      return;
    }

    if (savedLevel === 3 && context.secondAccountId) {
      const parentSecondCode =
        context.secondLevelCode ||
        accountRows.find(
          (a) => String(a.ID) === String(context.secondAccountId)
        )?.Account_Code ||
        "";
      const nextCode = getNextLevel3Code(accountRows, parentSecondCode);
      setThirdLevelCode(nextCode);
      setAccountCode(nextCode);
    }
  };

  useEffect(() => {
    if (
      !initialLoading &&
      AccountLevel === 1 &&
      (!FirstAccountId || FirstAccountId === "0" || FirstAccountId === 0)
    ) {
      applyLevel1Defaults(Accounts);
    }
  }, [Accounts, initialLoading, AccountLevel, FirstAccountId]);

  const renderAccountsFirst = () => {
    if (!Array.isArray(Accounts) || Accounts.length === 0) {
      return null;
    }
    return Accounts.filter((item) => item.iLevel === 1).map((item, index) => (
      <option value={item.ID} data-code={item.Account_Code} key={index}>
        {item.Account_Code} - {item.Account_Description}
      </option>
    ));
  };

  const renderAccountsSecond = () => {
    if (Accounts.length > 0) {
      
      let level2 = Accounts.filter(
        (item) => item.iLevel == 2 && item.Account_Code.startsWith(Level1Code) && item.Account_Code.length == 5
      );
      return level2.map((item, index) => {
        return (
          <option value={item.ID} data-code={item.Account_Code} key={index}>
           {item.Account_Code} - {item.Account_Description}
          </option>
        );
      });
    }
  };

  const renderAccountsThird = () => {
    if (Accounts.length > 0) {
      let level1 = Accounts.filter(
        (item) => item.iLevel == 3 && item.pAccountID == SecondAccountId
      );
      return level1.map((item, index) => {
        return (
          <option value={item.ID} data-code={item.Account_Code} key={index}>
            {item.Account_Code} - {item.Account_Description}
          </option>
        );
      });
    }
  };

  const handleFirstChange = (e) => {
    const selectedOptionCode =
      e.target.options[e.target.selectedIndex].dataset.code || "0";

    if (e.target.value === "0" || selectedOptionCode === "0") {
      setAccountLevel(1);
      setLevelID(1);
      setParentAccountID(0);
      setLevel1Status(false);
      setLevel2Status(true);
      setLevel3Status(true);
      setSecondLevelCode("");
      setThirdLevelCode("");
      setAccountDetail("");
      setAccountRemarks("");
      applyLevel1Defaults(Accounts);
      return;
    }

    setAccountLevel(2);
    setFirstAccountId(e.target.value);
    const firstcode = String(selectedOptionCode).padStart(2, "0");

    setLevelID(2);
    setParentAccountID(e.target.value);
    setLevel1Code(selectedOptionCode);

    const second_code = getNextLevel2Code(Accounts, selectedOptionCode);

    setSecondLevelCode(second_code);
    setAccountCode(second_code);
    setAccountDetail("");
    setAccountRemarks("");
    setLevel1Status(true);
    setLevel2Status(false);
    setFirstLevelCode(firstcode);
  };

  const handleSecondChange = (e) => {
    const selectedOptionCode =
      e.target.options[e.target.selectedIndex].dataset.code || "0";

    if (e.target.value === "0" || selectedOptionCode === "0") {
      setAccountLevel(2);
      setSecondAccountId(0);
      setLevel2Status(false);
      setLevel3Status(true);
      setThirdLevelCode("");
      setAccountDetail("");
      setAccountRemarks("");
      if (Level1Code) {
        const nextCode = getNextLevel2Code(Accounts, Level1Code);
        setSecondLevelCode(nextCode);
        setAccountCode(nextCode);
      }
      return;
    }

    setAccountLevel(3);
    setSecondAccountId(e.target.value);
    setSecondLevelCode(String(selectedOptionCode));

    const third_code = getNextLevel3Code(Accounts, selectedOptionCode);

    setThirdLevelCode(third_code);
    setAccountCode(third_code);
    setLevel1Status(true);
    setLevel2Status(true);
    setLevel3Status(false);
    setAccountDetail("");
    setAccountRemarks("");
  };

  const handleThirdChange = (e) => {
    //levelTwo
    //setThirdLevelCode(e.target.value);
    const dropdown = document.getElementById("levelThree");
    const numberOfItems = dropdown.options.length;
    const selectedOptionCode =
      e.target.options[e.target.selectedIndex].dataset.code;
    setThirdLevelCode(selectedOptionCode.toString().padStart(2, "0"));
  };

  const isDuplicateAccountName = (name) => {
    const key = String(name || "").trim().toLowerCase();
    if (!key) {
      return false;
    }
    return (Accounts || []).some(
      (item) =>
        !item.Is_Deleted &&
        String(item.Account_Description || "")
          .trim()
          .toLowerCase() === key
    );
  };

  const handleSaveAccount = () => {
    const accountName = AccountDetail.trim();
    if (!accountName) {
      toast.error("Please enter account details", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (isDuplicateAccountName(accountName)) {
      toast.error(
        "An account with this name already exists. Please use a different account name.",
        { position: toast.POSITION.TOP_RIGHT }
      );
      return;
    }

    const code = String(AccountCode || "").trim();
    if (!code) {
      toast.error("Account code is missing. Select level or reload the page.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!FYear) {
      toast.error(
        "Financial year is not set. Open Accounts → Change Year and select a year first.",
        { position: toast.POSITION.TOP_RIGHT }
      );
      return;
    }

    const savedLevel = AccountLevel;
    const saveContext = {
      firstAccountId: FirstAccountId,
      secondAccountId: SecondAccountId,
      level1Code: Level1Code,
      secondLevelCode: SecondLevelCode,
    };

    setSaving(true);
    const data = {
      UserID: UserID,
      AccessKey: AccessKey,
      MemberType: MemberType,
      Account_Code: code,
      Account_Description: accountName,
      Remarks: AccountRemarks,
      FYear: Number(FYear),
    };

    axios({
      method: "post",
      url: config.base_url + "Accounts/AddNewAccount",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then(async (response) => {
        if (response.data.status_code === 1) {
          toast.success(response.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
          setAccountDetail("");
          setAccountRemarks("");
          const rows = await loadAccounts({ silent: true });
          refreshAfterSave(rows, savedLevel, saveContext);
        } else {
          toast.error(response.data.status_message || "Could not save account.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() => {
        toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <AccountsMenu />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Accounts/AccountsDashboard">
                        Accounts
                      </NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Add New Account
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Add / Update Account</div>
              </div>
              <div className="col-md-6">
                <div className="page-right-actions">
                  <NavLink
                    to="/Accounts/ManageAccounts"
                    className="button button-white button-icon"
                  >
                    Manage Account
                    <i className="ri-add-line"></i>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="card">
              <div className="card-body">
                <p className="text-muted small mb-3">
                  Root accounts must use codes 10, 20, 30, 40, 50, 60, or 70. Keep the first dropdown on
                  &quot;1st Level&quot;, enter the account name, then Save. Year: {FYear}
                </p>
                {initialLoading ? (
                  <div className="text-center">
                    <Spinner animation="border" role="status"></Spinner>
                  </div>
                ) : (
                  <>
                    <div className="row align-items-center mb-3">
                      <div className="col-md-2">
                        <h5>
                          1<sup>st</sup> Level
                        </h5>
                      </div>
                      <div className="col-md-10">
                        <div className="row align-items-center">
                          <div className="col-md-3">
                            <div className="form-group mb-2">
                              <label>Select First Level</label>
                              <select
                                id="levelOne"
                                className="form-select"
                                required
                                value={String(FirstAccountId || "0")}
                                onChange={(e) => handleFirstChange(e)}
                              >
                                <option value="0" data-code="0">
                                  1st Level
                                </option>
                                {renderAccountsFirst()}
                              </select>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-group mb-2">
                              <label>Name of Account</label>
                              <input
                                type="text"
                                className="form-control"
                                onChange={(e) =>
                                  setAccountDetail(e.target.value)
                                }
                                disabled={Level1Status}
                              />
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <label>Remarks</label>
                              <input
                                type="text"
                                className="form-control"
                                onChange={(e) =>
                                  setAccountRemarks(e.target.value)
                                }
                                disabled={Level1Status}
                              />
                            </div>
                          </div>
                          <div className="col-md-2">{FirstLevelCode}</div>
                        </div>
                      </div>
                    </div>
                    <div className="row align-items-center mb-3">
                      <div className="col-md-2">
                        <h5>
                          2<sup>nd</sup> Level
                        </h5>
                      </div>
                      <div className="col-md-10">
                        <div className="row align-items-center">
                          <div className="col-md-3">
                            <div className="form-group mb-2">
                              <label>Select Second Level</label>
                              <select
                                id="levelTwo"
                                className="form-select"
                                required
                                value={String(SecondAccountId || "0")}
                                onChange={(e) => handleSecondChange(e)}
                              >
                                <option value="0" data-code="0">
                                  2nd Level
                                </option>
                                {renderAccountsSecond()}
                              </select>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-group mb-2">
                              <label>Name of Account</label>
                              <input
                                type="text"
                                className="form-control"
                                onChange={(e) =>
                                  setAccountDetail(e.target.value)
                                }
                                disabled={Level2Status}
                              />
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <label>Remarks</label>
                              <input
                                type="text"
                                className="form-control"
                                onChange={(e) =>
                                  setAccountRemarks(e.target.value)
                                }
                                disabled={Level2Status}
                              />
                            </div>
                          </div>
                          <div className="col-md-2">{SecondLevelCode}</div>
                        </div>
                      </div>
                    </div>
                    <div className="row align-items-center">
                      <div className="col-md-2">
                        <h5>
                          3<sup>rd</sup> Level
                        </h5>
                      </div>
                      <div className="col-md-10">
                        <div className="row align-items-center">
                          <div className="col-md-3">
                            
                          </div>
                          <div className="col-md-3">
                            <div className="form-group mb-2">
                              <label>Name of Account</label>
                              <input
                                type="text"
                                className="form-control"
                                onChange={(e) =>
                                  setAccountDetail(e.target.value)
                                }
                                disabled={Level3Status}
                              />
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <label>Remarks</label>
                              <input
                                type="text"
                                className="form-control"
                                onChange={(e) =>
                                  setAccountRemarks(e.target.value)
                                }
                                disabled={Level3Status}
                              />
                            </div>
                          </div>
                          <div className="col-md-2">{ThirdLevelCode}</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-end">
                      <button
                        className="button button-primary"
                        onClick={handleSaveAccount}
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save Account"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddNewAccount;
